import React from "react"
import PositionSection from "./PositionSection"
import TransparencySection from "./TransparencySection"
import TagSection from "./TagSection"
import AnimationSection from "./AnimationSection"
import ClickActionSection from "./ClickActionSection"
import RotateSection from "./RotateSection"
import ShowOnPageSection from "./ShowOnPageSection"
import ElementPosition from "./ElementPosition"
import HotspotFormMockup from "../hotspotSection/HotspotFormMockup"
import SectionCard from "../../SectionCard"
import { animationSummary, appearanceSummary, clickActionSummary, layoutSummary, metadataSummary, transformSummary } from "./sectionSummaries"

export const showsElementPosition = (toolType, editElement) => toolType === 'ImageToolbox' || toolType === 'EmbedToolbox'
    || editElement?.element_type === 6 || editElement?.element_type === 4;

const FormFieldsSection = ({ handleChange, scrollActive, tool, toolType, docData, editElement, setTool, totalPages, errorState, zoom, handleSubmitBtnState, sizeInContent = false }) => {
    // Hotspot (non-clone) gets its own sectioned layout
    const isHotspot = (toolType === 'HotspotToolbox' || editElement?.element_type === 9) && toolType !== 'Clone';
    if (isHotspot) {
        return (
            <HotspotFormMockup
                handleChange={handleChange}
                scrollActive={scrollActive}
                tool={tool}
                toolType={toolType}
                docData={docData}
                editElement={editElement}
                setTool={setTool}
                totalPages={totalPages}
                errorState={errorState}
                zoom={zoom}
                handleSubmitBtnState={handleSubmitBtnState}
            />
        );
    }

    const isImage = toolType === 'ImageToolbox' || editElement?.element_type === 4;
    const isClone = toolType === 'Clone';

    const showElementPosition = showsElementPosition(toolType, editElement) && !sizeInContent;

    const showTransparency = toolType === 'ImageToolbox' || editElement?.element_type === 4;

    const isText = toolType === 'TextToolbox' || editElement?.element_type === 2 || editElement?.element_type === 7;

    const showPage = tool?.id && (
        toolType === 'ImageToolbox' || toolType === 'Clone'
        || editElement?.element_type === 4 || isText
    );

    const showTransform = toolType === 'ImageToolbox' || toolType === 'Clone' || editElement?.element_type === 4;

    const showClickAction = toolType === 'ImageToolbox' || toolType === 'HotspotToolbox'
        || editElement?.element_type === 4 || editElement?.element_type === 9;

    // Clone reveals only the action already configured on the source element (see
    // ClickActionSection's per-row show/hidden classes) — if nothing is configured,
    // there's nothing to reveal, so skip the section instead of showing it empty.
    const hasClickAction = !isClone || Boolean(
        (tool?.action && tool?.action !== 'none') ||
        (tool?.action === 'none' && tool?.dropdown_data && Object.keys(tool.dropdown_data).length > 0) ||
        tool?.value?.page_link || tool?.value?.url || tool?.value?.phone || tool?.value?.mail ||
        tool?.value?.light_box || tool?.value?.kdf_title
    );

    const metadataErrors = isClone
        ? [errorState?.tagErr, errorState?.pageErr, errorState?.hrefErr, errorState?.ljErr, errorState?.kdfErr]
        : [errorState?.tagErr];

    return (
        <div className="hs-mockup">
            <SectionCard title="Appearance" variant="appearance" defaultOpen={true} summary={appearanceSummary(tool, showTransparency)}>
                {showTransparency && (
                    <TransparencySection
                        handleChange={handleChange}
                        tool={tool}
                    />
                )}
                <TagSection
                    handleChange={handleChange}
                    tool={tool}
                    totalPages={docData?.num_pages}
                    setTool={setTool}
                    errorState={errorState}
                    toolType={toolType}
                    editElement={editElement}
                    docData={docData}
                    handleSubmitBtnState={handleSubmitBtnState}
                    mode="color-only"
                />
            </SectionCard>

            <SectionCard title="Layout & Position" errors={sizeInContent ? [errorState?.pageErr] : [errorState?.dimensionError, errorState?.pageErr]} summary={layoutSummary(tool, !sizeInContent)}>
                <PositionSection
                    handleChange={handleChange}
                    tool={tool}
                    setTool={setTool}
                    errorState={errorState}
                    zoom={zoom}
                    showSize={!sizeInContent}
                >
                    {sizeInContent && showPage && (
                        <ShowOnPageSection
                            tool={tool}
                            totalPages={docData?.num_pages}
                            handleChange={handleChange}
                            errorState={errorState}
                            inline
                        />
                    )}
                </PositionSection>
                {showElementPosition && (
                    <ElementPosition
                        handleChange={handleChange}
                        tool={tool}
                        toolType={toolType}
                        editElement={editElement}
                    />
                )}
                {showPage && !sizeInContent && (
                    <ShowOnPageSection
                        tool={tool}
                        totalPages={docData?.num_pages}
                        handleChange={handleChange}
                        errorState={errorState}
                    />
                )}
            </SectionCard>

            {showTransform && (
                <SectionCard title="Transform" variant="transform" summary={transformSummary(tool)}>
                    <RotateSection
                        tool={tool}
                        setTool={setTool}
                        handleSubmitBtnState={handleSubmitBtnState}
                    />
                </SectionCard>
            )}

            {!isClone && (
                <SectionCard title="Animation" variant="animation" summary={animationSummary(tool)}>
                    <AnimationSection
                        handleChange={handleChange}
                        tool={tool}
                    />
                </SectionCard>
            )}

            {showClickAction && hasClickAction && (
                <SectionCard title="Click Action" variant="action" errors={[errorState?.pageErr, errorState?.hrefErr, errorState?.ljErr, errorState?.kdfErr]} summary={clickActionSummary(tool)}>
                    <ClickActionSection
                        totalPages={totalPages}
                        handleChange={handleChange}
                        scrollActive={scrollActive}
                        tool={tool}
                        currentDocId={docData?.doc_id}
                        setTool={setTool}
                        errorState={errorState}
                        pdfId={docData?.id}
                        toolType={toolType}
                    />
                </SectionCard>
            )}

            <SectionCard title="Metadata & Tracking" variant="metadata" errors={metadataErrors} summary={metadataSummary(tool)}>
                <TagSection
                    handleChange={handleChange}
                    tool={tool}
                    totalPages={docData?.num_pages}
                    setTool={setTool}
                    errorState={errorState}
                    toolType={toolType}
                    editElement={editElement}
                    docData={docData}
                    handleSubmitBtnState={handleSubmitBtnState}
                    mode={isClone ? "all" : "tags-only"}
                />
            </SectionCard>
        </div>
    );
}

export default FormFieldsSection
